import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Bell, BellOff, Check, CheckCheck, Trash2, Filter, Search } from 'lucide-react'
import useNotificationStore from '../../stores/useNotificationStore'

/**
 * Notifications Page - Full notifications list with details
 * Shows all notifications from Laravel notifications table
 */
function Notifications() {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    loading,
    error,
    fetchAllNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotificationStore()

  const [filter, setFilter] = useState('all') // 'all', 'unread', 'read'
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedNotification, setSelectedNotification] = useState(null)

  useEffect(() => {
    fetchAllNotifications()
  }, [])

  // Filter notifications
  const filteredNotifications = notifications.filter(notif => {
    // Apply status filter
    if (filter === 'unread' && notif.read_at) return false
    if (filter === 'read' && !notif.read_at) return false

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      const title = (notif.title || '').toLowerCase()
      const message = (notif.message || '').toLowerCase()
      return title.includes(search) || message.includes(search)
    }

    return true
  })

  const handleMarkAsRead = async (notificationId) => {
    await markAsRead(notificationId)
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  const handleNotificationClick = (notif) => {
    setSelectedNotification(notif)
    if (!notif.read_at) {
      handleMarkAsRead(notif.id)
    }
  }

  const formatTime = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)

    if (diffInSeconds < 60) return 'الآن'
    if (diffInSeconds < 3600) return `منذ ${Math.floor(diffInSeconds / 60)} دقيقة`
    if (diffInSeconds < 86400) return `منذ ${Math.floor(diffInSeconds / 3600)} ساعة`
    if (diffInSeconds < 604800) return `منذ ${Math.floor(diffInSeconds / 86400)} يوم`

    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getNotificationIcon = (type) => {
    // You can customize icons based on notification type
    if (type?.includes('Invoice')) return '🧾'
    if (type?.includes('Payment')) return '💰'
    if (type?.includes('Customer')) return '👤'
    if (type?.includes('Product')) return '📦'
    if (type?.includes('Transfer')) return '🚚'
    return '🔔'
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-6 sticky top-0 z-30 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowRight className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">الإشعارات</h1>
              <p className="text-sm text-white/80">
                {unreadCount > 0 ? `${unreadCount} غير مقروء` : 'جميع الإشعارات مقروءة'}
              </p>
            </div>
          </div>

          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <CheckCheck className="w-5 h-5" />
              <span className="text-sm">تعليم الكل</span>
            </button>
          )}
        </div>

        {/* Search and Filter */}
        <div className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="بحث في الإشعارات..."
              className="w-full pr-10 pl-4 py-3 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                filter === 'all'
                  ? 'bg-white text-blue-600 font-semibold'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              الكل ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                filter === 'unread'
                  ? 'bg-white text-blue-600 font-semibold'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              غير مقروء ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                filter === 'read'
                  ? 'bg-white text-blue-600 font-semibold'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              مقروء ({notifications.length - unreadCount})
            </button>
          </div>
        </div>
      </header>

      {/* Notifications List */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <BellOff className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchTerm ? 'لا توجد نتائج للبحث' : 'لا توجد إشعارات'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notif) => (
              <div
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border transition-all duration-200 cursor-pointer hover:shadow-md ${
                  !notif.read_at
                    ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-2xl">
                      {getNotificationIcon(notif.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className={`font-semibold text-base ${
                          !notif.read_at ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {notif.title || 'إشعار'}
                        </h3>
                        {!notif.read_at && (
                          <span className="inline-block w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></span>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                        {notif.message}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {formatTime(notif.created_at)}
                        </span>
                        {notif.read_at && (
                          <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            مقروء
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={() => setSelectedNotification(null)}
          ></div>
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-lg mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-50 max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-white">تفاصيل الإشعار</h2>
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-white/80">{formatTime(selectedNotification.created_at)}</p>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {selectedNotification.title}
                </h3>
              </div>

              {/* Message */}
              <div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {selectedNotification.message}
                </p>
              </div>

              {/* Additional Data */}
              {selectedNotification.data && Object.keys(selectedNotification.data).length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">معلومات إضافية</h4>
                  <div className="space-y-2 text-sm">
                    {Object.entries(selectedNotification.data).map(([key, value]) => {
                      // Skip title and message as they're already displayed
                      if (key === 'title' || key === 'message') return null

                      // Format value
                      let displayValue = value
                      if (typeof value === 'object') {
                        displayValue = JSON.stringify(value, null, 2)
                      }

                      return (
                        <div key={key} className="flex gap-2">
                          <span className="font-medium text-gray-600 dark:text-gray-400 min-w-[100px]">
                            {key}:
                          </span>
                          <span className="text-gray-900 dark:text-white flex-1">
                            {displayValue}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Type */}
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium">النوع:</span>
                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {selectedNotification.type?.split('\\').pop() || 'General'}
                </span>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                {selectedNotification.read_at ? (
                  <span className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCheck className="w-4 h-4" />
                    تم قراءته في {new Date(selectedNotification.read_at).toLocaleString('ar-EG')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                    <Bell className="w-4 h-4" />
                    غير مقروء
                  </span>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setSelectedNotification(null)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Notifications
